import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { CalendarIcon, Plus, X, Loader2, CheckCircle, Upload, FileText, Image, AlertCircle } from "lucide-react";
import { format, isBefore, startOfDay } from "date-fns";
import { it } from "date-fns/locale";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AddSagraPage = () => {
  const navigate = useNavigate();
  const [regioni, setRegioni] = useState([]);
  const [province, setProvince] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [specialitaInput, setSpecialitaInput] = useState("");
  const [locandinaFile, setLocandinaFile] = useState(null);
  const [uploadingLocandina, setUploadingLocandina] = useState(false);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    nome: "",
    descrizione: "",
    regione: "",
    provincia: "",
    comune: "",
    indirizzo: "",
    data_inizio: null,
    data_fine: null,
    orario_apertura: "",
    orario_chiusura: "",
    contatti: {
      nome_organizzatore: "",
      telefono: "",
      email: ""
    },
    coordinate: {
      lat: "",
      lng: ""
    },
    immagine_url: "",
    specialita: []
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchRegioni();
  }, []);

  useEffect(() => {
    if (formData.regione) {
      fetchProvince(formData.regione);
      setFormData(prev => ({ ...prev, provincia: "" }));
    }
  }, [formData.regione]);

  const fetchRegioni = async () => {
    try {
      setLoadingData(true);
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);
      
      const res = await axios.get(`${API}/regioni`, {
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      setRegioni(res.data.regioni);
    } catch (error) {
      console.error("Error fetching regioni:", error);
      if (error.code === 'ECONNABORTED' || error.message.includes('aborted')) {
        toast.error("Il server sta caricando. Riprova tra 10 secondi.");
      } else {
        toast.error("Errore nel caricamento delle regioni");
      }
    } finally {
      setLoadingData(false);
    }
  };

  const fetchProvince = async (regione) => {
    try {
      const res = await axios.get(`${API}/province/${encodeURIComponent(regione)}`);
      setProvince(res.data.province);
    } catch (error) {
      console.error("Error fetching province:", error);
      toast.error("Errore nel caricamento delle province");
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  const handleContattiChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      contatti: { ...prev.contatti, [field]: value }
    }));
  };

  const handleCoordinateChange = (field, value) => {
    const numValue = parseFloat(value);
    if (field === 'lat' && (numValue < -90 || numValue > 90)) {
      toast.error("Latitudine deve essere tra -90 e 90");
      return;
    }
    if (field === 'lng' && (numValue < -180 || numValue > 180)) {
      toast.error("Longitudine deve essere tra -180 e 180");
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      coordinate: { ...prev.coordinate, [field]: value }
    }));
  };

  const addSpecialita = () => {
    const trimmed = specialitaInput.trim();
    
    if (!trimmed) {
      toast.error("Inserisci una specialità");
      return;
    }
    
    if (trimmed.length < 2) {
      toast.error("Specialità troppo corta (min 2 caratteri)");
      return;
    }
    
    if (formData.specialita.includes(trimmed)) {
      toast.error("Specialità già aggiunta");
      return;
    }
    
    if (formData.specialita.length >= 10) {
      toast.error("Massimo 10 specialità");
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      specialita: [...prev.specialita, trimmed]
    }));
    setSpecialitaInput("");
    toast.success("Specialità aggiunta");
  };

  const removeSpecialita = (spec) => {
    setFormData(prev => ({
      ...prev,
      specialita: prev.specialita.filter(s => s !== spec)
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/jpg', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      toast.error("Formato non supportato. Usa JPG o PDF.");
      return;
    }

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error("File troppo grande. Dimensione massima: 10 MB");
      return;
    }

    setLocandinaFile(file);
    toast.success("Locandina selezionata");
  };

  const removeLocandina = () => {
    setLocandinaFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.nome.trim()) {
      newErrors.nome = "Nome richiesto";
    } else if (formData.nome.trim().length < 3) {
      newErrors.nome = "Nome troppo corto (min 3 caratteri)";
    } else if (formData.nome.trim().length > 100) {
      newErrors.nome = "Nome troppo lungo (max 100 caratteri)";
    }
    
    if (!formData.descrizione.trim()) {
      newErrors.descrizione = "Descrizione richiesta";
    } else if (formData.descrizione.trim().length < 10) {
      newErrors.descrizione = "Descrizione troppo corta (min 10 caratteri)";
    } else if (formData.descrizione.trim().length > 2000) {
      newErrors.descrizione = "Descrizione troppo lunga (max 2000 caratteri)";
    }
    
    if (!formData.regione) newErrors.regione = "Regione richiesta";
    if (!formData.provincia) newErrors.provincia = "Provincia richiesta";
    
    if (!formData.comune.trim()) {
      newErrors.comune = "Comune richiesto";
    } else if (formData.comune.trim().length < 2) {
      newErrors.comune = "Comune troppo corto (min 2 caratteri)";
    }
    
    if (!formData.indirizzo.trim()) {
      newErrors.indirizzo = "Indirizzo richiesto";
    } else if (formData.indirizzo.trim().length < 3) {
      newErrors.indirizzo = "Indirizzo troppo corto (min 3 caratteri)";
    }
    
    if (!formData.data_inizio) {
      newErrors.data_inizio = "Data inizio richiesta";
    }
    
    if (!formData.data_fine) {
      newErrors.data_fine = "Data fine richiesta";
    }
    
    if (formData.data_inizio && formData.data_fine) {
      const inizio = startOfDay(formData.data_inizio);
      const fine = startOfDay(formData.data_fine);
      
      if (isBefore(fine, inizio)) {
        newErrors.data_fine = "Data fine deve essere dopo o uguale a data inizio";
      }
    }
    
    if (formData.contatti.email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.contatti.email)) {
        newErrors.email = "Email non valida";
      }
    }
    
    if (formData.contatti.telefono.trim()) {
      const phoneRegex = /^[\d\s\+\-\(\)]+$/;
      if (!phoneRegex.test(formData.contatti.telefono) || formData.contatti.telefono.trim().length < 8) {
        newErrors.telefono = "Numero di telefono non valido";
      }
    }
    
    if (formData.immagine_url.trim()) {
      try {
        new URL(formData.immagine_url);
      } catch {
        newErrors.immagine_url = "URL non valido";
      }
    }
    
    if (formData.coordinate.lat && !formData.coordinate.lng) {
      newErrors.coordinate = "Se inserisci latitudine, inserisci anche longitudine";
    }
    if (formData.coordinate.lng && !formData.coordinate.lat) {
      newErrors.coordinate = "Se inserisci longitudine, inserisci anche latitudine";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error("Correggi gli errori nel form");
      const firstError = Object.keys(errors)[0];
      const element = document.getElementById(firstError);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setLoading(true);

    try {
      const payload = {
        ...formData,
        nome: formData.nome.trim(),
        descrizione: formData.descrizione.trim(),
        comune: formData.comune.trim(),
        indirizzo: formData.indirizzo.trim(),
        data_inizio: format(formData.data_inizio, "yyyy-MM-dd"),
        data_fine: format(formData.data_fine, "yyyy-MM-dd"),
        coordinate: formData.coordinate.lat && formData.coordinate.lng 
          ? { lat: parseFloat(formData.coordinate.lat), lng: parseFloat(formData.coordinate.lng) }
          : null,
        contatti: {
          nome_organizzatore: formData.contatti.nome_organizzatore.trim(),
          telefono: formData.contatti.telefono.trim(),
          email: formData.contatti.email.trim()
        }
      };

      const response = await axios.post(`${API}/sagre`, payload, {
        timeout: 30000
      });
      
      const sagraId = response.data.id;
      
      if (locandinaFile) {
        setUploadingLocandina(true);
        const formDataFile = new FormData();
        formDataFile.append('file', locandinaFile);
        
        try {
          await axios.post(`${API}/sagre/${sagraId}/locandina`, formDataFile, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 60000
          });
          toast.success("Locandina caricata con successo!");
        } catch (uploadError) {
          console.error("Error uploading locandina:", uploadError);
          toast.error("Sagra creata, ma errore nel caricamento della locandina");
        }
        setUploadingLocandina(false);
      }
      
      toast.success("Sagra aggiunta con successo! In attesa di approvazione.", {
        duration: 5000
      });
      
      setTimeout(() => {
        navigate(`/sagra/${sagraId}`);
      }, 1000);
      
    } catch (error) {
      console.error("Error creating sagra:", error);
      
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        toast.error("Il server sta caricando. Riprova tra 10 secondi.");
      } else if (error.response) {
        toast.error(`Errore: ${error.response.data.detail || 'Errore durante la creazione'}`);
      } else if (error.request) {
        toast.error("Impossibile contattare il server. Controlla la connessione.");
      } else {
        toast.error("Errore durante la creazione della sagra");
      }
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-lg font-medium">Caricamento...</p>
          <p className="text-sm text-muted-foreground mt-2">
            Al primo accesso può richiedere fino a 30 secondi
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30 py-8" data-testid="add-sagra-page">
      <div className="max-w-3xl mx-auto px-4">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
            Aggiungi una Sagra
          </h1>
          <p className="text-muted-foreground text-lg">
            Condividi la tua sagra con migliaia di appassionati
          </p>
        </div>

        <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
          <div className="text-sm text-blue-800 dark:text-blue-200">
            <p className="font-medium mb-1">La tua sagra sarà moderata</p>
            <p className="text-blue-700 dark:text-blue-300">
              Dopo l'invio, la sagra verrà revisionata dal nostro team prima di essere pubblicata.
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <Card className="border-0 shadow-soft">
            <CardHeader>
              <CardTitle>Informazioni Base</CardTitle>
              <CardDescription>I dettagli principali della sagra</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="nome">Nome Sagra *</Label>
                <Input
                  id="nome"
                  value={formData.nome}
                  onChange={(e) => handleInputChange("nome", e.target.value)}
                  placeholder="es. Sagra della Porchetta"
                  className={`mt-1.5 ${errors.nome ? 'border-destructive' : ''}`}
                  maxLength={100}
                />
                {errors.nome && <p className="text-destructive text-sm mt-1">{errors.nome}</p>}
                <p className="text-xs text-muted-foreground mt-1">{formData.nome.length}/100</p>
              </div>

              <div>
                <Label htmlFor="descrizione">Descrizione *</Label>
                <Textarea
                  id="descrizione"
                  value={formData.descrizione}
                  onChange={(e) => handleInputChange("descrizione", e.target.value)}
                  placeholder="Descrivi la sagra..."
                  rows={4}
                  className={`mt-1.5 ${errors.descrizione ? 'border-destructive' : ''}`}
                  maxLength={2000}
                />
                {errors.descrizione && <p className="text-destructive text-sm mt-1">{errors.descrizione}</p>}
                <p className="text-xs text-muted-foreground mt-1">{formData.descrizione.length}/2000</p>
              </div>

              <div>
                <Label>Specialità</Label>
                <div className="flex gap-2 mt-1.5">
                  <Input
                    value={specialitaInput}
                    onChange={(e) => setSpecialitaInput(e.target.value)}
                    placeholder="es. Porchetta"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSpecialita())}
                    maxLength={50}
                  />
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={addSpecialita}
                    disabled={formData.specialita.length >= 10}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {formData.specialita.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {formData.specialita.map((spec, i) => (
                      <Badge key={i} variant="secondary" className="rounded-full gap-1 pr-1">
                        {spec}
                        <button type="button" onClick={() => removeSpecialita(spec)} className="ml-1">
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="immagine_url">URL Immagine</Label>
                <Input
                  id="immagine_url"
                  value={formData.immagine_url}
                  onChange={(e) => handleInputChange("immagine_url", e.target.value)}
                  placeholder="https://..."
                  className={`mt-1.5 ${errors.immagine_url ? 'border-destructive' : ''}`}
                />
                {errors.immagine_url && <p className="text-destructive text-sm mt-1">{errors.immagine_url}</p>}
              </div>

              <div>
                <Label>Locandina</Label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".jpg,.jpeg,.pdf"
                  onChange={handleFileChange}
                  className="hidden"
                />
                {!locandinaFile ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full mt-1.5 gap-2 h-20 border-dashed"
                  >
                    <Upload className="w-5 h-5" />
                    Carica locandina (JPG/PDF, max 10MB)
                  </Button>
                ) : (
                  <div className="mt-1.5 p-3 bg-muted/50 rounded-lg flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {locandinaFile.type === 'application/pdf' ? <FileText className="w-5 h-5" /> : <Image className="w-5 h-5" />}
                      <div>
                        <p className="text-sm font-medium">{locandinaFile.name}</p>
                        <p className="text-xs text-muted-foreground">{(locandinaFile.size / 1024 / 1024).toFixed(2)} MB</p>
                      </div>
                    </div>
                    <Button type="button" variant="ghost" size="icon" onClick={removeLocandina}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-soft">
            <CardHeader>
              <CardTitle>Località</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Regione *</Label>
                  <Select value={formData.regione} onValueChange={(value) => handleInputChange("regione", value)}>
                    <SelectTrigger className={`mt-1.5 ${errors.regione ? 'border-destructive' : ''}`}>
                      <SelectValue placeholder="Seleziona regione" />
                    </SelectTrigger>
                    <SelectContent>
                      {regioni.map((reg) => <SelectItem key={reg} value={reg}>{reg}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {errors.regione && <p className="text-destructive text-sm mt-1">{errors.regione}</p>}
                </div>

                <div>
                  <Label>Provincia *</Label>
                  <Select value={formData.provincia} onValueChange={(value) => handleInputChange("provincia", value)} disabled={!formData.regione}>
                    <SelectTrigger className={`mt-1.5 ${errors.provincia ? 'border-destructive' : ''}`}>
                      <SelectValue placeholder="Seleziona provincia" />
                    </SelectTrigger>
                    <SelectContent>
                      {province.map((prov) => <SelectItem key={prov} value={prov}>{prov}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {errors.provincia && <p className="text-destructive text-sm mt-1">{errors.provincia}</p>}
                </div>
              </div>

              <div>
                <Label htmlFor="comune">Comune *</Label>
                <Input
                  id="comune"
                  value={formData.comune}
                  onChange={(e) => handleInputChange("comune", e.target.value)}
                  placeholder="es. Ariccia"
                  className={`mt-1.5 ${errors.comune ? 'border-destructive' : ''}`}
                />
                {errors.comune && <p className="text-destructive text-sm mt-1">{errors.comune}</p>}
              </div>

              <div>
                <Label htmlFor="indirizzo">Indirizzo *</Label>
                <Input
                  id="indirizzo"
                  value={formData.indirizzo}
                  onChange={(e) => handleInputChange("indirizzo", e.target.value)}
                  placeholder="es. Piazza di Corte"
                  className={`mt-1.5 ${errors.indirizzo ? 'border-destructive' : ''}`}
                />
                {errors.indirizzo && <p className="text-destructive text-sm mt-1">{errors.indirizzo}</p>}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="lat">Latitudine</Label>
                  <Input
                    id="lat"
                    type="number"
                    step="any"
                    value={formData.coordinate.lat}
                    onChange={(e) => handleCoordinateChange("lat", e.target.value)}
                    placeholder="41.7197"
                    className="mt-1.5"
                    min="-90"
                    max="90"
                  />
                </div>
                <div>
                  <Label htmlFor="lng">Longitudine</Label>
                  <Input
                    id="lng"
                    type="number"
                    step="any"
                    value={formData.coordinate.lng}
                    onChange={(e) => handleCoordinateChange("lng", e.target.value)}
                    placeholder="12.6722"
                    className="mt-1.5"
                    min="-180"
                    max="180"
                  />
                </div>
              </div>
              {errors.coordinate && <p className="text-destructive text-sm mt-1">{errors.coordinate}</p>}
            </CardContent>
          </Card>

          <Card className="border-0 shadow-soft">
            <CardHeader>
              <CardTitle>Date e Orari</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Data Inizio *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className={`w-full mt-1.5 justify-start ${errors.data_inizio ? 'border-destructive' : ''}`}>
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.data_inizio ? format(formData.data_inizio, "d MMMM yyyy", { locale: it }) : "Seleziona data"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={formData.data_inizio}
                        onSelect={(date) => handleInputChange("data_inizio", date)}
                        locale={it}
                        disabled={(date) => isBefore(date, startOfDay(new Date()))}
                      />
                    </PopoverContent>
                  </Popover>
                  {errors.data_inizio && <p className="text-destructive text-sm mt-1">{errors.data_inizio}</p>}
                </div>

                <div>
                  <Label>Data Fine *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className={`w-full mt-1.5 justify-start ${errors.data_fine ? 'border-destructive' : ''}`}>
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.data_fine ? format(formData.data_fine, "d MMMM yyyy", { locale: it }) : "Seleziona data"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={formData.data_fine}
                        onSelect={(date) => handleInputChange("data_fine", date)}
                        locale={it}
                        disabled={(date) => isBefore(date, formData.data_inizio || startOfDay(new Date()))}
                      />
                    </PopoverContent>
                  </Popover>
                  {errors.data_fine && <p className="text-destructive text-sm mt-1">{errors.data_fine}</p>}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="orario_apertura">Orario Apertura</Label>
                  <Input
                    id="orario_apertura"
                    type="time"
                    value={formData.orario_apertura}
                    onChange={(e) => handleInputChange("orario_apertura", e.target.value)}
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label htmlFor="orario_chiusura">Orario Chiusura</Label>
                  <Input
                    id="orario_chiusura"
                    type="time"
                    value={formData.orario_chiusura}
                    onChange={(e) => handleInputChange("orario_chiusura", e.target.value)}
                    className="mt-1.5"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-soft">
            <CardHeader>
              <CardTitle>Contatti Organizzatore</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="nome_organizzatore">Nome Organizzatore</Label>
                <Input
                  id="nome_organizzatore"
                  value={formData.contatti.nome_organizzatore}
                  onChange={(e) => handleContattiChange("nome_organizzatore", e.target.value)}
                  placeholder="es. Pro Loco Ariccia"
                  className="mt-1.5"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="telefono">Telefono</Label>
                  <Input
                    id="telefono"
                    type="tel"
                    value={formData.contatti.telefono}
                    onChange={(e) => handleContattiChange("telefono", e.target.value)}
                    placeholder="06 9330758"
                    className={`mt-1.5 ${errors.telefono ? 'border-destructive' : ''}`}
                  />
                  {errors.telefono && <p className="text-destructive text-sm mt-1">{errors.telefono}</p>}
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.contatti.email}
                    onChange={(e) => handleContattiChange("email", e.target.value)}
                    placeholder="info@sagra.it"
                    className={`mt-1.5 ${errors.email ? 'border-destructive' : ''}`}
                  />
                  {errors.email && <p className="text-destructive text-sm mt-1">{errors.email}</p>}
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/cerca")}
              className="rounded-full"
            >
              Annulla
            </Button>
            <Button
              type="submit"
              disabled={loading || uploadingLocandina}
              className="rounded-full gap-2 px-8"
            >
              {loading || uploadingLocandina ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {uploadingLocandina ? "Caricamento..." : "Salvataggio..."}
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Pubblica Sagra
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddSagraPage;